/**
 * 診断SPA から Shopify へ戻ってきたお客様を、カート投入 → チェックアウトまで運ぶ。
 *
 * 設計書 D45② の実装。診断・見積りと購入の紐付けは cart attributes で行う。
 * D46 で顧客ログインを必須にしないと決めたため、ログイン ID では紐付けられない。
 *
 * 受け取るクエリ:
 *   model   … 型番（必須）。例 CS-X406D-W
 *   plan    … set_replace（工事つき・取替え／既定） / set_new（工事つき・新規取り付け）
 *             / solo（本体のみ）。`set` は取替えの旧称として受け付ける（既存URL互換）
 *   quote   … AiCON の見積りID → cart attribute __aicon_quote_id
 *   session … 診断セッションID → cart attribute __aicon_session_id
 *
 * 属性名は backend/src/services/shopify/order-webhook.service.ts の
 * ATTR_QUOTE_ID / ATTR_SESSION_ID と一致させること。片方だけ変えると
 * 注文 webhook が紐付けを回収できず、診断と注文が無関係な2件になる。
 *
 * 「__」前置のプライベート属性は Liquid からも Ajax API からも読み出せない（公式）。
 * ＝書けたことをストアフロント側で検証する手段が無い。確認は注文 webhook 側の
 * 構造化ログで行う。ここでは書き込みの HTTP ステータスだけを見る。
 */

/**
 * 本体バリアントの option 値。
 * backend/src/services/shopify/types.ts の BODY_WITH_INSTALL_VALUE / BODY_SHIPPED_VALUE と
 * **完全一致**させること。ここは `options.includes()` の完全一致で引いているため、
 * 片方だけ変えると「お見積もりの購入方法が選べませんでした」で復帰導線が止まる。
 */
const OPTION_BODY_WITH_INSTALL = '本体（工事つき）';
const OPTION_SOLO = '本体のみ';

/** 工事商品（本体とは別商品）。types.ts の INSTALL_PRODUCT_HANDLE と一致させること */
const INSTALL_HANDLE = 'aicon-standard-install';
/**
 * 延長保証（任意・2026-08-20）。
 * ★ 復帰時に必ず消す ★
 * 保証の金額は**本体価格の帯**で決まる。前回の機種で入れた保証行が残ったまま
 * 別の機種を入れると、違う帯の金額で売ることになる（帯の差は最大 15,000円）。
 * 診断からの復帰は機種が変わる導線そのものなので、本体と一緒に必ず消す。
 */
const WARRANTY_HANDLE = 'aicon-extended-warranty';
/**
 * 追加工事（2026-08-20）。
 * 前回の診断で入った追加工事が残ったまま別の機種を入れると、
 * 関係のない機種の追加工事を請求することになる。復帰時は必ず消す。
 */
const ADDITIONAL_HANDLE = 'aicon-additional-work';

/** plan パラメータを本体の option 値へ。未知の値と旧称 `set` は工事つきに寄せる */
function optionForPlan(plan) {
  return plan === 'solo' ? OPTION_SOLO : OPTION_BODY_WITH_INSTALL;
}

/**
 * 工事バリアントを選ぶ。
 *
 * ★ 容量帯はタグから取る ★
 * ストアフロントの /products/<handle>.js は metafield を返さないため、
 * 容量帯は商品タグとして公開してある
 * （backend/src/services/shopify/types.ts の installSetTierTag）。
 * 容量帯タグが1つに定まらないときは null を返して止める（呼び出し側が決済を止める）。
 * 当てずっぽうで選ぶと段の差（最大 11,000円）を取り違える。
 *
 * ★ 工事費は4段になった（2026-09-01）★
 * `aicon-install-small / -large` は**配管延長と2階→1階が使う別軸**として残っており、
 * 工事費の容量帯は `aicon-installset-t1..t4` が正。ここは後者だけを見る。
 * ラベルの対応は backend の INSTALL_SET_LABELS が正で、下の表はその写し。
 * **バリアント名を変えるときは両方を直すこと**（片方だけ直すと選択が外れて
 * 決済が止まる。止まるのは安全側だが、原因が分かりにくい）。
 */
const SET_TIER_TAG_PREFIX = 'aicon-installset-';
const SET_TIER_LABELS = {
  t1: '2.2〜2.8kW',
  t2: '3.6〜4.0kW',
  t3: '5.6kW',
  t4: '6.3kW以上',
};

function findInstallVariant(installProduct, tags, plan) {
  const tiers = (tags || [])
    .map((t) => String(t))
    .filter((t) => Object.keys(SET_TIER_LABELS).some((k) => t === `${SET_TIER_TAG_PREFIX}${k}`));

  // ★ 1つに定まらなければ止める ★
  //   0個 = まだ同期されていない商品。
  //   2個 = 容量帯が変わったのに古いタグが残っている（backend の product-differ が
  //         直すが、同期が走るまでの間はこの状態があり得る）。
  //   どちらも「どの工事費が正しいか分からない」状態で、当てずっぽうで選ぶと
  //   small と large の差 5,000円 を取り違える。安い側にも高い側にも倒さない。
  if (tiers.length !== 1) return null;

  const wantLabel = SET_TIER_LABELS[tiers[0].slice(SET_TIER_TAG_PREFIX.length)];
  if (!wantLabel) return null;
  const wantNew = plan === 'set_new';
  return (
    (installProduct.variants || []).find((v) => {
      const title = String(v.title || '');
      // ★ 写真診断済みバリアントは選ばない（2026-08-20）★
      //   工事商品は 4→8 バリアントになった。除外しないと `.find()` が
      //   バリアントの並び順まかせになり、順序が変わった瞬間に割引価格
      //   （−3,000円）で売ることになる。いまは通常版が先に並ぶので
      //   たまたま正しいだけで、依存してはいけない。
      //
      //   この導線に割引を効かせるかどうかは別の判断（診断SPA 側の総額と
      //   揃える必要があるため、変えるときは confirmQuote の割引と併せて見ること）。
      if (title.includes('写真診断済み')) return false;
      const isNew = title.includes('新規');
      return title.includes(wantLabel) && isNew === wantNew;
    }) || null
  );
}

/**
 * 型番から Shopify の handle を作る。
 * backend/src/services/shopify/product-mapper.ts の toHandle と同じ規則。
 * 変えると商品が見つからなくなるため、両方を揃えて変更すること。
 */
function toHandle(modelNumber) {
  return modelNumber.toLowerCase().replace(/[^a-z0-9]+/g, '-');
}

/**
 * ロケール付きURLの接頭辞（例 /ja/）。Shopify が注入する window.Shopify.routes.root。
 * 万一取れない場合でもカート投入を諦めないよう '/' に落とす。
 */
/**
 * 本体と工事を1組として結ぶ ID を作る。
 * 名前と使い方は assets/aicon-install-quote.js の newAiconSetId と揃えること
 * （読む側は assets/aicon-cart-pair.js の groupBySet）。
 */
function newAiconSetId() {
  try {
    const uuid = window.crypto?.randomUUID?.();
    if (uuid) return uuid.slice(0, 8);
  } catch (_e) {
    // crypto を触れない環境。下の組み立てに落ちる
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
}

function routeRoot() {
  return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
}

class AiconReturn extends HTMLElement {
  connectedCallback() {
    this.statusEl = this.querySelector('[data-aicon-return-status]');
    this.errorEl = this.querySelector('[data-aicon-return-error]');
    this.errorTextEl = this.querySelector('[data-aicon-return-error-text]');
    this.run();
  }

  setStatus(text) {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  /**
   * 失敗しても行き止まりにしない。お客様には一覧への導線を残す。
   * 原因は console に出す（お客様には技術情報を見せない）。
   */
  fail(userMessage, cause) {
    if (cause) console.error('[aicon-return]', cause);
    if (this.statusEl) this.statusEl.hidden = true;
    if (this.errorTextEl) this.errorTextEl.textContent = userMessage;
    if (this.errorEl) this.errorEl.hidden = false;
  }

  /**
   * カートから指定商品の行をすべて取り除く。
   *
   * `cart/change.js` は行を1つずつしか変えられないので、見つかった数だけ順に呼ぶ。
   * 行を消すと後ろの line 番号がずれるため、番号ではなく `key`（行の識別子）で指定する。
   *
   * @param {number} productId `/products/<handle>.js` が返す product.id
   */
  async removeExistingLines(productId, extraHandles = []) {
    try {
      const res = await fetch(`${routeRoot()}cart.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) {
        console.warn('[aicon-return] カートを読めませんでした', res.status);
        return;
      }
      const cart = await res.json();
      // 工事行・保証行も一緒に消す。本体だけ消すと、商品ページで先に入れていたお客様の
      // カートに前回の行が残り、ここで足すぶんと合わせて2行になる。
      // 工事は 26,590円〜で二重請求になり、保証は前の機種の帯のまま残る。必ず消す。
      const targets = (cart.items || []).filter(
        (item) =>
          item.product_id === productId ||
          extraHandles.includes(item.handle) ||
          extraHandles.includes(item.product_handle),
      );
      if (targets.length === 0) return;

      for (const item of targets) {
        const changeRes = await fetch(`${routeRoot()}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: item.key, quantity: 0 }),
        });
        if (!changeRes.ok) {
          console.warn('[aicon-return] 既存行を消せませんでした', item.key, changeRes.status);
        }
      }
    } catch (e) {
      // 掃除は best-effort。ここで例外を投げると購入まで巻き添えで止まる
      console.warn('[aicon-return] 既存行の掃除に失敗', e);
    }
  }

  async run() {
    const params = new URLSearchParams(window.location.search);
    const model = (params.get('model') || '').trim();
    const plan = (params.get('plan') || 'set_replace').trim();
    const quoteId = (params.get('quote') || '').trim();
    const sessionId = (params.get('session') || '').trim();

    if (!model) {
      this.fail('お見積もりの情報を受け取れませんでした。お手数ですが、一覧から機種をお選びください。', 'model パラメータが空');
      return;
    }

    try {
      /*
       * ★ handle は診断から渡されたものを優先する（2026-09-17）★
       *   型番から作ると、admin で型番を訂正した瞬間に products/<handle>.js が
       *   404 になり、ここで復帰が止まる（2026-09-15 に三菱30機種で実発生）。
       *   backend の products.shopifyHandle が正で、診断が ?handle= で運んでくる。
       *   古いリンク（handle 無し）は従来どおり型番から作る。
       */
      const handle = (params.get('handle') || '').trim() || toHandle(model);

      this.setStatus('お選びいただいた機種を確認しています…');
      const res = await fetch(`${routeRoot()}products/${handle}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) {
        this.fail(
          `お見積もりの機種（${model}）が見つかりませんでした。在庫状況が変わった可能性があります。一覧からお選びください。`,
          `GET products/${handle}.js → ${res.status}`,
        );
        return;
      }
      const product = await res.json();

      const wantOption = optionForPlan(plan);
      const variant = (product.variants || []).find((v) => (v.options || []).includes(wantOption));
      if (!variant) {
        this.fail(
          'お見積もりの購入方法が選べませんでした。商品ページからお選びください。',
          `variant not found: ${wantOption} in ${handle}`,
        );
        return;
      }
      if (variant.available === false) {
        this.fail(
          `${product.title} は現在お取り扱いできません。一覧から他の機種をご覧ください。`,
          `variant ${variant.id} unavailable`,
        );
        return;
      }

      // ★ 工事は別商品の別行（2026-08-19 / D3）★
      // 以前は「標準工事セット」バリアント1行で本体＋工事の総額だった。工事費を
      // 機種から切り離したため、工事つきを選んだお客様には工事の行も入れないと
      // **工事費が丸ごと請求漏れになる**（取替え small で 26,590円）。
      let installVariant = null;
      if (plan !== 'solo') {
        const insRes = await fetch(`${routeRoot()}products/${INSTALL_HANDLE}.js`, {
          headers: { Accept: 'application/json' },
        });
        if (insRes.ok) {
          installVariant = findInstallVariant(await insRes.json(), product.tags, plan);
        }
        // 工事を入れられないまま本体だけ決済させない。
        // 本体だけ買われると、工事の請求を後から追いかけることになる。
        if (!installVariant || installVariant.available === false) {
          this.fail(
            '標準工事をお申し込みできませんでした。お手数ですが、商品ページからお進みください。',
            `install variant not resolved: plan=${plan} tags=${JSON.stringify(product.tags)}`,
          );
          return;
        }
      }

      // ★ 同じ商品の行が既にあれば先に消す ★
      // cart/add.js は「追加」なので、診断へ入る前に商品ページで「カートに入れる」を
      // 押していたお客様が戻ってくると、同じエアコンが2行になる。数量2で決済されると
      // 実際には1台しか要らないのに2台分を請求することになる。
      // 購入方法（工事つき / 本体のみ）が違っても同一商品なので、
      // variant ではなく product 単位で消す。
      //
      // ★ 属性を書く前に消すこと ★
      // これが最後の1行だった場合、カートは一度空になる。空カートで属性が保持される
      // 保証は公式に無いため、消してから書く順にしておく。逆にすると
      // `__aicon_quote_id` が落ちたまま注文が立ち、webhook が紐付けを回収できない。
      //
      // 掃除に失敗しても購入は止めない。重複より「買えない」ほうが損失が大きく、
      // 重複はカート画面でお客様が気づいて直せる。
      await this.removeExistingLines(product.id, [INSTALL_HANDLE, WARRANTY_HANDLE, ADDITIONAL_HANDLE]);

      // 属性はカート単位。カート投入より先に書く。
      // 後にすると、投入直後にお客様がチェックアウトへ進んだ場合に紐付けが漏れる。
      //
      // ★値が無いときも必ず空文字で上書きする。
      //   cart/update.js の attributes は「渡したキーだけ」を更新する（マージ）。
      //   条件付きで送ると、前回の診断で載せた見積りIDがカートに残ったまま
      //   今回の注文に付いてしまい、無関係な見積りと注文が紐付く。
      //   空文字を渡すと属性が削除される。
      {
        this.setStatus('お見積もりの内容を引き継いでいます…');
        const attributes = {
          __aicon_quote_id: quoteId || '',
          __aicon_session_id: sessionId || '',
        };

        const attrRes = await fetch(`${routeRoot()}cart/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes }),
        });
        // ここで落ちたら購入させない。紐付けの無い注文が立つと、
        // どの診断・見積りの注文か後から辿れなくなる（webhook が回収できない）。
        if (!attrRes.ok) {
          this.fail(
            'お見積もりの引き継ぎに失敗しました。お手数ですが、もう一度お試しください。',
            `POST cart/update.js → ${attrRes.status}`,
          );
          return;
        }
      }

      this.setStatus('カートに追加しています…');
      const setId = newAiconSetId();
      const addRes = await fetch(`${routeRoot()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          // ★ この投入ぶんを1組として結ぶ（2026-08-25・複数台）★
          //   assets/aicon-cart-pair.js が、片方が減らされたときに同じ組から相手を引く。
          //   組ID が無いと、機種の違う2台で本体と工事を取り違えて消す。
          items: [
            { id: variant.id, quantity: 1, properties: { _aicon_set: setId } },
            ...(installVariant ? [{ id: installVariant.id, quantity: 1, properties: { _aicon_set: setId } }] : []),
          ],
        }),
      });
      if (!addRes.ok) {
        this.fail(
          'カートに追加できませんでした。お手数ですが、商品ページからお試しください。',
          `POST cart/add.js → ${addRes.status}`,
        );
        return;
      }

      this.setStatus('お支払い手続きへ進みます…');
      window.location.assign(`${routeRoot()}checkout`);
    } catch (e) {
      this.fail('通信に失敗しました。電波状況をご確認のうえ、もう一度お試しください。', e);
    }
  }
}

if (!customElements.get('aicon-return')) {
  customElements.define('aicon-return', AiconReturn);
}
